/**
 * PeopleTab — collaborators and roles (Phase 3.4).
 * Role-based grouping: Owner, Collaborators, Pending invites.
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Avatar,
  Button,
  TextField,
  Select,
  MenuItem,
  Divider,
  Chip,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { Plan, PlanRole } from '../../types/generated/graphql';
import { colors } from '../../theme/colors';

interface PeopleTabProps {
  plan: Plan;
  onInvite?: (email: string, role: string) => void;
}

export const PeopleTab: React.FC<PeopleTabProps> = ({ plan, onInvite }) => {
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('editor');

  const roles: PlanRole[] = plan.roles ?? [];
  const participants = plan.participants ?? [];

  const handleInvite = () => {
    if (!inviteEmail.trim()) return;
    onInvite?.(inviteEmail.trim(), inviteRole);
    setInviteEmail('');
  };

  return (
    <Box>
      {/* Roles section */}
      {roles.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Roles
          </Typography>
          <Stack spacing={1}>
            {roles.map((r) => (
              <Paper key={r.roleId} variant="outlined" sx={{ px: 2, py: 1.5, borderRadius: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: colors.blue[100], color: colors.blue[700], fontSize: 14 }}>
                    {r.name.charAt(0)}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.name}</Typography>
                    {r.description && (
                      <Typography variant="caption" color="text.secondary">{r.description}</Typography>
                    )}
                  </Box>
                  {r.required && <Chip label="Required" size="small" color="warning" variant="outlined" />}
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}

      {/* Participants */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Collaborators ({participants.length})
        </Typography>
        {participants.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No collaborators yet. Invite someone below.
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={1}>
            {participants.map((p) => (
              <Paper key={p.id} variant="outlined" sx={{ px: 2, py: 1.5, borderRadius: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: colors.gray[200], fontSize: 14 }}>
                    {p.name.charAt(0)}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{p.email || ''}</Typography>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Invite section */}
      <Box>
        <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Invite
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
          <TextField
            size="small"
            placeholder="Email address"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            sx={{ flex: 1 }}
          />
          <Select size="small" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} sx={{ minWidth: 120 }}>
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="editor">Editor</MenuItem>
            <MenuItem value="viewer">Viewer</MenuItem>
          </Select>
          <Button
            variant="contained"
            size="small"
            startIcon={<PersonAddIcon />}
            onClick={handleInvite}
            disabled={!inviteEmail.trim()}
          >
            Send invite
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default PeopleTab;
